import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Plus,
  Search,
  Printer,
  Copy,
  Trash2,
  Eye,
  ChevronDown,
  Package,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Pencil,
} from "lucide-react";

type Bol = {
  id: number;
  bol_number: string;
  date: string;
  customer_name: string;
  destination_address: string;
  destination_city: string;
  destination_state: string;
  material_type: string;
  gross_weight: number | null;
  tare_weight: number | null;
  net_weight: number | null;
  net_weight_tons: number | null;
  status: string;
  load_type: string;
  client_tag: string | null;
  work_order_id: number | null;
  job_id: number | null;
  created_at: string;
};

type BolsResponse = {
  bols: Bol[];
  total: number;
  totalTons: number;
  thisWeekCount: number;
};

type SortField = "bol_number" | "date" | "customer_name" | "material_type" | "net_weight_tons" | "status" | "load_type" | "client_tag";
type SortDir = "asc" | "desc";

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  draft: { label: "Draft", bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400" },
  completed: { label: "Completed", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
  delivered: { label: "Delivered", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
};

const CLIENT_TAG_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  vanguard: { label: "Vanguard", bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400" },
  willcox: { label: "Willcox", bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" },
  "3lag": { label: "3LAG", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  flower_gods: { label: "Flower Gods", bg: "bg-pink-100 dark:bg-pink-900/30", text: "text-pink-700 dark:text-pink-400" },
};

const LOAD_TYPE_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  Outbound: { label: "Outbound", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
  Inbound: { label: "Inbound", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  Transfer: { label: "Transfer", bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400" },
  Return: { label: "Return", bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
};

function Badge({ config }: { config: { label: string; bg: string; text: string } }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <ArrowUpDown size={12} className="text-muted-foreground/40" />;
  return sortDir === "asc" ? (
    <ArrowUp size={12} className="text-forest-500" />
  ) : (
    <ArrowDown size={12} className="text-forest-500" />
  );
}

export default function BolList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [clientTagFilter, setClientTagFilter] = useState("all");
  const [loadTypeFilter, setLoadTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionsOpen, setActionsOpen] = useState<number | null>(null);

  // Sorting
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir(field === "date" ? "desc" : "asc");
    }
  }

  // Fetch BOLs
  const { data, isLoading, error } = useQuery<BolsResponse>({
    queryKey: ["bols", statusFilter, dateFilter, clientTagFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (dateFilter !== "all") params.set("dateFilter", dateFilter);
      if (clientTagFilter !== "all") params.set("client_tag", clientTagFilter);
      const res = await apiFetch(`/api/ops/bols?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load BOLs (${res.status})`);
      return res.json();
    },
    enabled: !!token,
  });

  // Fetch Job numbers for lookup
  const { data: jobData } = useQuery<{ id: number; job_number: string }[]>({
    queryKey: ["job-numbers"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/jobs", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const json = await res.json();
      return (Array.isArray(json) ? json : json.jobs ?? []).map((j: any) => ({ id: j.id, job_number: j.job_number }));
    },
    enabled: !!token,
    staleTime: 60000,
  });
  const jobMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const j of jobData ?? []) m.set(j.id, j.job_number);
    return m;
  }, [jobData]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await apiFetch("/api/ops/bols/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error("Failed to delete BOLs");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bols"] });
      setSelectedIds(new Set());
      setShowDeleteConfirm(false);
    },
  });

  const bols = data?.bols ?? [];
  const totalBols = data?.total ?? 0;
  const totalTons = data?.totalTons ?? 0;
  const thisWeekCount = data?.thisWeekCount ?? 0;

  // Client-side search + load type filter + sort
  const filteredBols = useMemo(() => {
    let result = bols;

    // Load type filter
    if (loadTypeFilter !== "all") {
      result = result.filter((b) => b.load_type === loadTypeFilter);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((bol) => {
        const haystack = [bol.bol_number, bol.customer_name, bol.material_type, bol.destination_city, bol.client_tag]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    // Sort
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "bol_number":
          cmp = (a.bol_number || "").localeCompare(b.bol_number || "");
          break;
        case "date":
          cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "customer_name":
          cmp = (a.customer_name || "").localeCompare(b.customer_name || "");
          break;
        case "material_type":
          cmp = (a.material_type || "").localeCompare(b.material_type || "");
          break;
        case "net_weight_tons":
          cmp = (Number(a.net_weight_tons) || 0) - (Number(b.net_weight_tons) || 0);
          break;
        case "status":
          cmp = (a.status || "").localeCompare(b.status || "");
          break;
        case "load_type":
          cmp = (a.load_type || "").localeCompare(b.load_type || "");
          break;
        case "client_tag":
          cmp = (a.client_tag || "").localeCompare(b.client_tag || "");
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [bols, search, loadTypeFilter, sortField, sortDir]);

  const allSelected = filteredBols.length > 0 && filteredBols.every((b) => selectedIds.has(b.id));

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredBols.map((b) => b.id)));
    }
  }

  function toggleOne(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handlePrintPdf(id: number) {
    window.open(`/api/ops/bols/${id}/pdf?token=${encodeURIComponent(token ?? "")}`, "_blank");
  }

  function handleDeleteSingle(id: number) {
    setSelectedIds(new Set([id]));
    setShowDeleteConfirm(true);
  }

  const thClass = "px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground select-none";
  const thSortable = `${thClass} cursor-pointer hover:text-foreground transition-colors group`;

  return (
    <div className="space-y-3">
      {/* Toolbar: title + inline stats + filters + new button */}
      <div className="bg-card border border-border rounded-xl">
        {/* Top row: title, stats, action */}
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-bold text-foreground">Bill of Lading</h1>
            {!isLoading && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                <span><span className="font-semibold text-foreground">{totalBols}</span> total</span>
                <span className="text-border">|</span>
                <span><span className="font-semibold text-foreground">{Number(totalTons).toFixed(1)}</span>t</span>
                <span className="text-border">|</span>
                <span><span className="font-semibold text-foreground">{thisWeekCount}</span> this week</span>
              </div>
            )}
          </div>
          <Link
            to="/app/ops/bols/new"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            <Plus size={14} strokeWidth={2.5} />
            New BOL
          </Link>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
          {/* Search */}
          <label className="flex-1 min-w-[180px] flex items-center gap-2 px-2.5 bg-background border border-border rounded-lg">
            <Search size={13} className="text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search BOL#, customer, material..."
              className="w-full py-1.5 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60"
            />
          </label>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 px-2.5 bg-background border border-border rounded-lg text-xs text-foreground"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="delivered">Delivered</option>
          </select>

          {/* Load Type filter */}
          <select
            value={loadTypeFilter}
            onChange={(e) => setLoadTypeFilter(e.target.value)}
            className="h-8 px-2.5 bg-background border border-border rounded-lg text-xs text-foreground"
          >
            <option value="all">All Types</option>
            <option value="Inbound">Inbound</option>
            <option value="Outbound">Outbound</option>
            <option value="Transfer">Transfer</option>
            <option value="Return">Return</option>
          </select>

          {/* Date filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="h-8 px-2.5 bg-background border border-border rounded-lg text-xs text-foreground"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="3months">Last 3 Months</option>
          </select>

          {/* Client tag filter */}
          <select
            value={clientTagFilter}
            onChange={(e) => setClientTagFilter(e.target.value)}
            className="h-8 px-2.5 bg-background border border-border rounded-lg text-xs text-foreground"
          >
            <option value="all">All Clients</option>
            <option value="vanguard">Vanguard</option>
            <option value="willcox">Willcox</option>
            <option value="3lag">3LAG</option>
            <option value="flower_gods">Flower Gods</option>
          </select>

          {/* Filtered count */}
          {!isLoading && filteredBols.length !== bols.length && (
            <span className="text-[11px] text-muted-foreground">
              {filteredBols.length} of {bols.length}
            </span>
          )}
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 px-3 pb-3 pt-1 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {selectedIds.size} selected
            </span>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold rounded-md transition-colors"
            >
              <Trash2 size={12} />
              Delete
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-muted-foreground">Loading BOLs...</div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-sm text-red-600 dark:text-red-400">
              {error instanceof Error ? error.message : "Failed to load BOLs"}
            </p>
          </div>
        ) : filteredBols.length === 0 ? (
          <div className="p-16 text-center">
            <Package size={40} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              {bols.length === 0 ? "No BOLs yet. Create your first one." : "No BOLs match your filters."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-muted/40">
                <tr className="border-b border-border">
                  <th className="px-3 py-2.5 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="rounded border-border"
                    />
                  </th>
                  <th className={thSortable} onClick={() => toggleSort("bol_number")}>
                    <span className="inline-flex items-center gap-1">
                      BOL # <SortIcon field="bol_number" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={thSortable} onClick={() => toggleSort("date")}>
                    <span className="inline-flex items-center gap-1">
                      Date <SortIcon field="date" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={thSortable} onClick={() => toggleSort("customer_name")}>
                    <span className="inline-flex items-center gap-1">
                      Customer <SortIcon field="customer_name" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={thClass}>Destination</th>
                  <th className={thSortable} onClick={() => toggleSort("material_type")}>
                    <span className="inline-flex items-center gap-1">
                      Material <SortIcon field="material_type" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={`${thSortable} text-right`} onClick={() => toggleSort("net_weight_tons")}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      Tonnage <SortIcon field="net_weight_tons" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={thSortable} onClick={() => toggleSort("status")}>
                    <span className="inline-flex items-center gap-1">
                      Status <SortIcon field="status" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={thSortable} onClick={() => toggleSort("load_type")}>
                    <span className="inline-flex items-center gap-1">
                      Load <SortIcon field="load_type" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={thSortable} onClick={() => toggleSort("client_tag")}>
                    <span className="inline-flex items-center gap-1">
                      Client <SortIcon field="client_tag" sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className={thClass}>Job</th>
                  <th className="px-3 py-2 w-[120px] text-right" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredBols.map((bol) => {
                  const statusCfg = STATUS_CONFIG[bol.status] || STATUS_CONFIG.draft;
                  const clientCfg = bol.client_tag ? CLIENT_TAG_CONFIG[bol.client_tag] : null;
                  const loadCfg = LOAD_TYPE_CONFIG[bol.load_type] || LOAD_TYPE_CONFIG.Outbound;
                  const dest = [bol.destination_city, bol.destination_state].filter(Boolean).join(", ");

                  return (
                    <tr
                      key={bol.id}
                      onClick={() => navigate(`/app/ops/bols/${bol.id}`)}
                      className={`border-b border-border/50 last:border-0 transition-colors cursor-pointer ${
                        selectedIds.has(bol.id) ? "bg-forest-50/50 dark:bg-forest-950/20" : "hover:bg-muted/30"
                      }`}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(bol.id)}
                          onChange={() => toggleOne(bol.id)}
                          className="rounded border-border"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/app/ops/bols/${bol.id}`} className="font-mono font-semibold text-forest-600 dark:text-forest-400 hover:underline">
                          {bol.bol_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap tabular-nums">
                        {new Date(bol.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </td>
                      <td className="px-3 py-2 font-medium text-foreground max-w-[160px] truncate">{bol.customer_name || "-"}</td>
                      <td className="px-3 py-2 text-muted-foreground max-w-[140px] truncate">
                        {dest || "-"}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground max-w-[140px] truncate">
                        {bol.material_type || "-"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {bol.net_weight_tons != null && Number(bol.net_weight_tons) > 0 ? (
                          <span className="font-semibold text-foreground tabular-nums">
                            {Number(bol.net_weight_tons).toFixed(2)}
                            <span className="text-[11px] text-muted-foreground ml-0.5">t</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground/40">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge config={statusCfg} />
                      </td>
                      <td className="px-3 py-2">
                        <Badge config={loadCfg} />
                      </td>
                      <td className="px-3 py-2">
                        {clientCfg ? <Badge config={clientCfg} /> : <span className="text-muted-foreground/40">-</span>}
                      </td>
                      <td className="px-3 py-2">
                        {bol.job_id && jobMap.get(bol.job_id) ? (
                          <Link
                            to={`/app/ops/jobs/${bol.job_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-mono text-xs text-forest-600 dark:text-forest-400 hover:underline"
                          >
                            {jobMap.get(bol.job_id)}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground/40">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <div className="relative flex items-center justify-end gap-0.5">
                          <Link
                            to={`/app/ops/bols/${bol.id}`}
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                            title="View"
                          >
                            <Eye size={14} />
                          </Link>
                          <Link
                            to={`/app/ops/bols/${bol.id}/edit`}
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </Link>
                          <button
                            onClick={() => handlePrintPdf(bol.id)}
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                            title="Print PDF"
                          >
                            <Printer size={14} />
                          </button>
                          <button
                            onClick={() => setActionsOpen(actionsOpen === bol.id ? null : bol.id)}
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                            title="More"
                          >
                            <ChevronDown size={14} />
                          </button>

                          {/* Dropdown */}
                          {actionsOpen === bol.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setActionsOpen(null)}
                              />
                              <div className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
                                <Link
                                  to={`/app/ops/bols/new?duplicate=${bol.id}`}
                                  className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
                                  onClick={() => setActionsOpen(null)}
                                >
                                  <Copy size={14} />
                                  Duplicate
                                </Link>
                                <div className="border-t border-border my-1" />
                                <button
                                  onClick={() => {
                                    setActionsOpen(null);
                                    handleDeleteSingle(bol.id);
                                  }}
                                  className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors w-full text-left"
                                >
                                  <Trash2 size={14} />
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Trash2 size={20} className="text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Delete BOL{selectedIds.size > 1 ? "s" : ""}</h3>
                  <p className="text-sm text-muted-foreground">
                    This will permanently delete {selectedIds.size} BOL{selectedIds.size > 1 ? "s" : ""}. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedIds(new Set());
                }}
                className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(Array.from(selectedIds))}
                disabled={deleteMutation.isPending}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
